import type { FaqItem } from "@cleanbudget/contract/faq-item";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { cn } from "@cleanbudget/ui/lib/utils";
import { IconAlertCircle, IconArrowDown, IconArrowUp, IconGripVertical, IconPlus } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type DragEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { ButtonLink } from "@/components/button-link";
import { PageHeader } from "@/components/page-header";
import { PublishedBadge } from "@/components/published-badge";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";

const faqQuery = () => orpc.faqItem.list.queryOptions();

/**
 * Veelgestelde vragen, in the order the shop shows them. A question moves by dragging its handle or
 * with its arrow buttons (keyboard and phone); every move saves the whole order at once. The new
 * order shows straight away and goes back if saving fails.
 */
export const Route = createFileRoute("/_admin/content/faq/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(faqQuery()),
	component: FaqPage,
});

function FaqPage() {
	const { t } = useTranslation("faqItem");
	const { t: tContent } = useTranslation("content");
	const queryClient = useQueryClient();
	const { data: items } = useSuspenseQuery(faqQuery());
	const [problem, setProblem] = useState<"failed" | "outdated">();
	const [announcement, setAnnouncement] = useState("");
	const [dragging, setDragging] = useState<string>();
	const [over, setOver] = useState<string>();

	const reorderOptions = orpc.faqItem.reorder.mutationOptions();
	const reorder = useMutation({
		...reorderOptions,
		// One save at a time, in the order the moves were made.
		scope: { id: "faq-order" },
		onMutate: async ({ ids }) => {
			setProblem(undefined);
			await queryClient.cancelQueries({ queryKey: faqQuery().queryKey });
			const previous = queryClient.getQueryData(faqQuery().queryKey);
			if (previous) {
				const byId = new Map(previous.map((item) => [item.id, item]));
				queryClient.setQueryData(
					faqQuery().queryKey,
					ids.flatMap((id) => byId.get(id) ?? []),
				);
			}
			return { previous };
		},
		onSuccess: (saved) => {
			// A later move is still on its way: its own answer will be the order to show.
			if (queryClient.isMutating({ mutationKey: reorderOptions.mutationKey }) <= 1) {
				queryClient.setQueryData(faqQuery().queryKey, saved);
			}
		},
		onError: (error, _input, context) => {
			if (context?.previous) queryClient.setQueryData(faqQuery().queryKey, context.previous);
			setProblem(apiErrorTag(error) === "FaqOrderOutdated" ? "outdated" : "failed");
			return queryClient.invalidateQueries({ queryKey: faqQuery().queryKey });
		},
	});

	const move = (id: string, to: number, refocus?: "up" | "down") => {
		const from = items.findIndex((item) => item.id === id);
		const item = items[from];
		if (!item || to < 0 || to >= items.length || to === from) return;
		const ids = items.map((entry) => entry.id);
		ids.splice(from, 1);
		ids.splice(to, 0, id);
		reorder.mutate({ ids });
		setAnnouncement(t("moved", { question: item.question, position: to + 1, total: items.length }));
		if (refocus) {
			// The moved row may have been taken out of the page and put back: keep focus on its button,
			// or on the other one when this one is gone (moved to the very top or bottom).
			requestAnimationFrame(() => {
				const atEdge = (refocus === "up" && to === 0) || (refocus === "down" && to === items.length - 1);
				const wanted = atEdge ? (refocus === "up" ? "down" : "up") : refocus;
				document.getElementById(`faq-${id}-${wanted}`)?.focus();
			});
		}
	};

	const dropOn = (event: DragEvent, targetId: string) => {
		event.preventDefault();
		if (dragging && dragging !== targetId) {
			move(
				dragging,
				items.findIndex((item) => item.id === targetId),
			);
		}
		setDragging(undefined);
		setOver(undefined);
	};

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/content">{tContent("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
				actions={
					<ButtonLink to="/content/faq/new">
						<IconPlus aria-hidden />
						{t("new.title")}
					</ButtonLink>
				}
			/>
			{problem ? (
				<Alert variant="destructive">
					<IconAlertCircle aria-hidden />
					<AlertDescription>
						{match(problem)
							.with("failed", () => t("reorderFailed"))
							.with("outdated", () => t("reorderOutdated"))
							.exhaustive()}
					</AlertDescription>
				</Alert>
			) : null}
			{items.length === 0 ? (
				<p className="rounded-2xl bg-card p-10 text-center text-muted-foreground ring-1 ring-foreground/10">
					{t("empty")}
				</p>
			) : (
				<div className="flex flex-col gap-3">
					<p className="text-sm text-muted-foreground">{t("orderHint")}</p>
					<ol
						aria-label={t("list")}
						className="divide-y divide-border overflow-hidden rounded-2xl bg-card ring-1 ring-foreground/10"
					>
						{items.map((item, index) => (
							<FaqRow
								key={item.id}
								item={item}
								position={index + 1}
								first={index === 0}
								last={index === items.length - 1}
								dragging={dragging === item.id}
								over={over === item.id && dragging !== item.id}
								onMoveUp={() => move(item.id, index - 1, "up")}
								onMoveDown={() => move(item.id, index + 1, "down")}
								onDragStart={(event) => {
									event.dataTransfer.effectAllowed = "move";
									event.dataTransfer.setData("text/plain", item.id);
									setDragging(item.id);
								}}
								onDragEnd={() => {
									setDragging(undefined);
									setOver(undefined);
								}}
								onDragOver={(event) => {
									if (!dragging) return;
									event.preventDefault();
									event.dataTransfer.dropEffect = "move";
									setOver(item.id);
								}}
								onDrop={(event) => dropOn(event, item.id)}
							/>
						))}
					</ol>
				</div>
			)}
			<p aria-live="polite" className="sr-only">
				{announcement}
			</p>
		</div>
	);
}

function FaqRow({
	item,
	position,
	first,
	last,
	dragging,
	over,
	onMoveUp,
	onMoveDown,
	onDragStart,
	onDragEnd,
	onDragOver,
	onDrop,
}: {
	item: FaqItem;
	position: number;
	first: boolean;
	last: boolean;
	dragging: boolean;
	over: boolean;
	onMoveUp: () => void;
	onMoveDown: () => void;
	onDragStart: (event: DragEvent) => void;
	onDragEnd: () => void;
	onDragOver: (event: DragEvent) => void;
	onDrop: (event: DragEvent) => void;
}) {
	const { t } = useTranslation("faqItem");
	const question = item.question;
	return (
		<li
			onDragOver={onDragOver}
			onDrop={onDrop}
			className={cn(
				"flex items-center gap-2 px-2 py-2 transition-colors sm:gap-3 sm:px-3",
				dragging && "opacity-50",
				over && "bg-muted",
			)}
		>
			{/* The mouse's way to move a row; keyboard and touch use the arrow buttons. */}
			{/* biome-ignore lint/a11y/noStaticElementInteractions: a drag handle only, the buttons are the accessible way */}
			<span
				draggable
				onDragStart={onDragStart}
				onDragEnd={onDragEnd}
				title={t("drag", { question })}
				className="hidden cursor-grab touch-none rounded-lg p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing sm:block"
			>
				<IconGripVertical className="size-4" aria-hidden />
			</span>
			<span className="w-6 shrink-0 text-right text-sm text-muted-foreground tabular-nums">{position}</span>
			<span className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
				<Link
					to="/content/faq/$id"
					params={{ id: item.id }}
					className="min-w-0 font-medium break-words underline-offset-2 hover:underline sm:flex-1"
				>
					{question}
				</Link>
				<PublishedBadge published={item.published} />
			</span>
			<span className="flex shrink-0 gap-1">
				<Button
					id={`faq-${item.id}-up`}
					variant="ghost"
					size="icon-sm"
					disabled={first}
					onClick={onMoveUp}
					aria-label={t("moveUp", { question })}
				>
					<IconArrowUp aria-hidden />
				</Button>
				<Button
					id={`faq-${item.id}-down`}
					variant="ghost"
					size="icon-sm"
					disabled={last}
					onClick={onMoveDown}
					aria-label={t("moveDown", { question })}
				>
					<IconArrowDown aria-hidden />
				</Button>
			</span>
		</li>
	);
}
